import AppShell from '@/components/AppShell'
import { appRoutes } from '@/constants'
import {
  AlertTriangle,
  ArrowLeft,
  Home,
  Lock,
  Search,
  Shield
} from 'lucide-react'
import { Button } from '@quest-lock/ui/button'
import React from 'react'

interface ErrorPageProps {
  error?: string
  title?: string
  description?: string
  showBackButton?: boolean
  showNavigationOptions?: boolean
}

const ErrorPage: React.FC<ErrorPageProps> = ({
  error = 'An unexpected error occurred',
  title = 'Quest Interrupted',
  description = 'The ancient magic has encountered a disturbance. Fear not, brave adventurer - your journey can continue.',
  showBackButton = true,
  showNavigationOptions = true
}) => {
  const safeNavigate = (path: string | number) => {
    if (typeof path === 'string') {
      window.location.href = path
    } else {
      window.history.back()
    }
  }

  return (
    <AppShell>
      <div className="bg-shire-light font-body text-shire-dark min-h-screen">
        <div className="flex min-h-screen items-center justify-center px-6 py-12 pt-48">
          <div className="w-full max-w-2xl text-center">
            <div className="mx-auto mb-8 flex size-20 items-center justify-center rounded-full bg-red-500 shadow-lg">
              <AlertTriangle className="size-10 text-white" />
            </div>

            <h1 className="font-heading text-shire-dark mb-4 text-4xl md:text-5xl">
              {title}
            </h1>

            <p className="text-shire-bark mb-6 text-lg md:text-xl">
              {description}
            </p>

            {error && (
              <div className="mb-8 rounded-xl border-2 border-red-200 bg-red-50 p-6">
                <div className="flex items-start space-x-4">
                  <div className="rounded-full bg-red-200 p-3">
                    <Shield className="size-6 text-red-700" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-heading mb-2 text-lg font-bold text-red-800">
                      Error Details
                    </h4>
                    <p className="break-words text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {showBackButton && (
              <div className="mb-12 flex flex-col gap-4 sm:flex-row sm:justify-center">
                <Button
                  onClick={() => safeNavigate(-1)}
                  variant="default"
                  size="lg"
                  className="inline-flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="size-4" />
                  Retrace Steps
                </Button>

                <Button
                  onClick={() => safeNavigate(appRoutes.vault)}
                  variant="outline"
                  size="lg"
                  className="inline-flex items-center justify-center gap-2"
                >
                  <Home className="size-4" />
                  Return to Vault
                </Button>
              </div>
            )}

            {showNavigationOptions && (
              <div className="border-shire-bark/20 rounded-xl border-2 bg-white/40 p-8">
                <h2 className="font-heading text-shire-dark mb-6 text-xl">
                  Choose Your Next Path
                </h2>
                <div className="grid gap-4 sm:grid-cols-3">
                  <button
                    onClick={() => safeNavigate(appRoutes.home)}
                    className="border-shire-bark/20 hover:bg-shire-stone/60 flex flex-col items-center gap-2 rounded-lg border bg-white/60 p-4 transition-colors"
                  >
                    <Home className="text-shire-bark size-5" />
                    <span className="text-shire-dark text-sm font-medium">
                      Village Square
                    </span>
                  </button>

                  <button
                    onClick={() => safeNavigate(appRoutes.autoLockPlanner)}
                    className="border-shire-bark/20 hover:bg-shire-stone/60 flex flex-col items-center gap-2 rounded-lg border bg-white/60 p-4 transition-colors"
                  >
                    <Lock className="text-shire-bark size-5" />
                    <span className="text-shire-dark text-sm font-medium">
                      Forge New Lock
                    </span>
                  </button>

                  <button
                    onClick={() => safeNavigate(appRoutes.vault)}
                    className="border-shire-bark/20 hover:bg-shire-stone/60 flex flex-col items-center gap-2 rounded-lg border bg-white/60 p-4 transition-colors"
                  >
                    <Search className="text-shire-bark size-5" />
                    <span className="text-shire-dark text-sm font-medium">
                      My Vault
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  )
}

export default ErrorPage
